<script>
import CustomerLayout from '@/Layouts/CustomerLayout.vue';
export default { layout: CustomerLayout };
</script>

<script setup>
import { computed, ref } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { CalendarDays, ChevronLeft, ChevronRight, RotateCcw, SlidersHorizontal } from '@lucide/vue';
import TransactionHistoryList from '@/Components/Customer/TransactionHistoryList.vue';

const props = defineProps({ transactions: Object, filters: Object, today: String });
const selectedType = ref(props.filters.type || 'all');
const startDate = ref(props.filters.start_date || '');
const endDate = ref(props.filters.end_date || '');
const calendarOpen = ref(false);
const initialDate = new Date(`${startDate.value || props.today}T00:00:00`);
const visibleMonth = ref(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));

const dateKey = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const parseDate = value => value ? new Date(`${value}T00:00:00`) : null;
const displayDate = value => parseDate(value)?.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' }) || '';
const rangeLabel = computed(() => startDate.value ? `${displayDate(startDate.value)}${endDate.value ? ` - ${displayDate(endDate.value)}` : ' - Pilih tanggal akhir'}` : 'Pilih rentang tanggal');
const monthLabel = computed(() => visibleMonth.value.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' }));
const days = computed(() => {
  const year = visibleMonth.value.getFullYear();
  const month = visibleMonth.value.getMonth();
  const firstOffset = (new Date(year, month, 1).getDay() + 6) % 7;
  const count = new Date(year, month + 1, 0).getDate();
  return [...Array(firstOffset).fill(null), ...Array.from({ length: count }, (_, index) => new Date(year, month, index + 1))];
});
const isSelected = day => [startDate.value, endDate.value].includes(dateKey(day));
const isInRange = day => startDate.value && endDate.value && day > parseDate(startDate.value) && day < parseDate(endDate.value);

function selectDate(day) {
  const key = dateKey(day);
  if (!startDate.value || endDate.value) {
    startDate.value = key;
    endDate.value = '';
    return;
  }
  if (day < parseDate(startDate.value)) {
    endDate.value = startDate.value;
    startDate.value = key;
  } else {
    endDate.value = key;
  }
}
function changeMonth(delta) { visibleMonth.value = new Date(visibleMonth.value.getFullYear(), visibleMonth.value.getMonth() + delta, 1); }
function applyFilters() {
  if (startDate.value && !endDate.value) return;
  calendarOpen.value = false;
  router.get(route('transactions.index'), { type: selectedType.value, start_date: startDate.value || undefined, end_date: endDate.value || undefined }, { preserveState: true, preserveScroll: true, replace: true });
}
function resetDatesToToday() { startDate.value = props.today; endDate.value = props.today; }
function resetFilters() { selectedType.value = 'all'; resetDatesToToday(); calendarOpen.value = false; router.get(route('transactions.index'), {}, { replace: true }); }
</script>

<template>
  <Head title="Riwayat Transaksi" />
  <div class="space-y-6">
    <header>
      <h1 class="text-xl sm:text-2xl font-black tracking-tight text-slate-900">Riwayat Transaksi</h1>
      <p class="text-xs sm:text-sm text-slate-500 mt-1">Catatan aktivitas transaksi digital Anda</p>
    </header>

    <section class="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs">
      <div class="flex items-center gap-2 mb-4"><SlidersHorizontal class="w-4 h-4 text-indigo-600" /><h2 class="text-xs font-bold text-slate-900">Filter transaksi</h2></div>
      <div class="grid grid-cols-1 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_auto] gap-3 items-end">
        <div class="relative">
          <label class="block text-[11px] font-bold text-slate-600 mb-1.5">Rentang tanggal</label>
          <button type="button" @click="calendarOpen = !calendarOpen" class="w-full min-h-11 px-3 rounded-xl border border-slate-300 bg-white flex items-center justify-between gap-2 text-xs text-left hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20">
            <span :class="startDate ? 'text-slate-800 font-semibold' : 'text-slate-400'">{{ rangeLabel }}</span><CalendarDays class="w-4 h-4 text-indigo-600 shrink-0" />
          </button>
          <div v-if="calendarOpen" class="absolute z-30 mt-2 w-full min-w-[300px] max-w-sm rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
            <div class="flex items-center justify-between mb-3"><button type="button" @click="changeMonth(-1)" class="p-2 rounded-lg hover:bg-slate-100"><ChevronLeft class="w-4 h-4" /></button><strong class="text-xs capitalize">{{ monthLabel }}</strong><button type="button" @click="changeMonth(1)" class="p-2 rounded-lg hover:bg-slate-100"><ChevronRight class="w-4 h-4" /></button></div>
            <div class="grid grid-cols-7 text-center text-[10px] font-bold text-slate-400 mb-1"><span v-for="name in ['Sen','Sel','Rab','Kam','Jum','Sab','Min']" :key="name">{{ name }}</span></div>
            <div class="grid grid-cols-7 gap-y-1">
              <span v-for="(day, index) in days" :key="index" class="h-9">
                <button v-if="day" type="button" @click="selectDate(day)" :class="[isSelected(day) ? 'bg-indigo-600 text-white font-bold' : isInRange(day) ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-slate-100 text-slate-700']" class="w-full h-9 rounded-lg text-xs transition">{{ day.getDate() }}</button>
              </span>
            </div>
            <div class="flex items-center justify-between gap-3 pt-3 mt-3 border-t border-slate-100"><button type="button" @click="resetDatesToToday" class="text-[11px] font-bold text-slate-500 hover:text-slate-800">Kembali ke hari ini</button><button type="button" :disabled="startDate && !endDate" @click="calendarOpen = false" class="px-3 py-2 rounded-lg bg-indigo-600 text-white text-[11px] font-bold disabled:opacity-40">Selesai</button></div>
          </div>
        </div>
        <div>
          <label for="transaction-type" class="block text-[11px] font-bold text-slate-600 mb-1.5">Jenis transaksi</label>
          <select id="transaction-type" v-model="selectedType" class="w-full min-h-11 px-3 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 outline-none">
            <option value="all">Semua Transaksi</option><option value="product">Transaksi Produk</option><option value="topup">Tambah Saldo</option>
          </select>
        </div>
        <button type="button" :disabled="startDate && !endDate" @click="applyFilters" class="min-h-11 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold disabled:opacity-40 active:scale-[0.98]">Terapkan</button>
      </div>
      <button v-if="filters.start_date !== today || filters.end_date !== today || filters.type !== 'all'" type="button" @click="resetFilters" class="mt-3 flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-indigo-700"><RotateCcw class="w-3.5 h-3.5" /> Reset semua filter</button>
    </section>

    <section class="space-y-4">
      <div class="flex items-center justify-between"><p class="text-xs text-slate-500"><strong class="text-slate-800">{{ transactions.total }}</strong> transaksi ditemukan</p></div>
      <TransactionHistoryList :transactions="transactions.data" empty-title="Tidak Ada Transaksi" empty-description="Tidak ada transaksi pada rentang dan jenis yang dipilih." />
      <nav v-if="transactions.last_page > 1" class="flex items-center justify-between pt-2">
        <Link v-if="transactions.prev_page_url" :href="transactions.prev_page_url" preserve-scroll class="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-xs font-bold hover:border-indigo-300">Sebelumnya</Link><span v-else />
        <span class="text-[11px] text-slate-500">Halaman {{ transactions.current_page }} dari {{ transactions.last_page }}</span>
        <Link v-if="transactions.next_page_url" :href="transactions.next_page_url" preserve-scroll class="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-xs font-bold hover:border-indigo-300">Berikutnya</Link><span v-else />
      </nav>
    </section>
  </div>
</template>
